import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SortingState, PaginationState, OnChangeFn, flexRender, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, Loader2, PlusCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { PriceListItem, PriceListItemCreate, PriceListItemUpdate } from '@/app/types/pricing';
import { Item } from '@/app/types/item';
import { ListQueryParams, PaginatedResponse } from '@/app/types/api';
import { listPriceListItems, addPriceListItem, updatePriceListItem, removePriceListItem } from '@/app/services/pricingService';
import { listItems } from '@/app/services/itemService';
import { columnsPriceListItem, dispatchPriceListItemEvent } from './columnsPriceListItem';
import PriceListItemForm from './PriceListItemForm';

// Reusable DataTable component
interface DataTableProps<TData, TValue> {
  columns: any[];
  data: TData[];
  isLoading?: boolean;
  pagination?: PaginationState;
  pageCount?: number;
  onPaginationChange?: OnChangeFn<PaginationState>;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
}

const ReusableDataTable = <TData, TValue>({ 
  columns, 
  data, 
  isLoading, 
  pagination, 
  pageCount, 
  onPaginationChange, 
  sorting, 
  onSortingChange 
}: DataTableProps<TData, TValue>) => {
  const table = useReactTable({
    data,
    columns,
    pageCount: pageCount ?? -1,
    state: { sorting, pagination },
    manualPagination: true,
    manualSorting: true,
    onPaginationChange,
    onSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map(row => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No items found in this price list.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-end space-x-2 py-4">
        <span className="text-sm text-muted-foreground">
          Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

interface PriceListItemManagerProps {
  priceListId: number;
  priceListName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PriceListItemManager: React.FC<PriceListItemManagerProps> = ({
  priceListId,
  priceListName,
  open,
  onOpenChange,
}) => {
  const queryClient = useQueryClient();
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [search, setSearch] = useState('');

  // State for Add Item Popover/Form
  const [addItemPopoverOpen, setAddItemPopoverOpen] = useState(false);
  const [selectedItemToAdd, setSelectedItemToAdd] = useState<Item | null>(null);
  const [newItemPrice, setNewItemPrice] = useState<string>('');
  const [newItemDiscountPercent, setNewItemDiscountPercent] = useState<string>('');
  const [newItemDiscountAmount, setNewItemDiscountAmount] = useState<string>('');
  const [newItemIsTaxInclusive, setNewItemIsTaxInclusive] = useState<boolean>(false);

  // State for Edit/Delete
  const [editingItem, setEditingItem] = useState<PriceListItem | null>(null);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState<PriceListItem | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  // Query Params
  const listItemsParams = useMemo<ListQueryParams>(() => ({
    skip: pagination.pageIndex * pagination.pageSize,
    limit: pagination.pageSize,
    search_query: search || undefined,
    ...(sorting.length > 0 && { sort_by: sorting[0].id, sort_dir: sorting[0].desc ? 'desc' : 'asc' }),
  }), [pagination, sorting, search]);

  // Fetch Items in this Price List
  const {
    data: priceListItemsResponse,
    isLoading: isLoadingListItems,
    isError: isListItemsError,
    error: listItemsError,
  } = useQuery<PaginatedResponse<PriceListItem>>({
    queryKey: ['priceListItems', priceListId, listItemsParams],
    queryFn: () => listPriceListItems(priceListId, listItemsParams),
    enabled: open && !!priceListId,
  });
  
  const pageCount = priceListItemsResponse ? Math.ceil(priceListItemsResponse.total / pagination.pageSize) : 0;

  // Fetch All Items for the 'Add Item' Select/Command
  const { data: allItemsResponse, isLoading: isLoadingAllItems } = useQuery<PaginatedResponse<Item>>({
    queryKey: ['allItemsForPricing', priceListId],
    queryFn: () => listItems({ limit: 500 }),
    enabled: open,
    staleTime: 5 * 60 * 1000, // Cache for 5 mins
  });
  
  const allItems = allItemsResponse?.items ?? [];
  // Filter items already in the current price list
  const itemsAlreadyInList = useMemo(() => 
    new Set(priceListItemsResponse?.items.map(pli => pli.item_id)), 
    [priceListItemsResponse]
  );
  
  const availableItemsToAdd = useMemo(() => 
    allItems.filter(item => !itemsAlreadyInList.has(item.id)), 
    [allItems, itemsAlreadyInList]
  );

  // Mutations
  const addItemMutation = useMutation({
    mutationFn: (data: PriceListItemCreate) => addPriceListItem(priceListId, data),
    onSuccess: () => {
      toast.success('Item added to price list');
      queryClient.invalidateQueries({ queryKey: ['priceListItems', priceListId] });
      setSelectedItemToAdd(null);
      setNewItemPrice('');
      setNewItemDiscountPercent('');
      setNewItemDiscountAmount('');
      setNewItemIsTaxInclusive(false);
      setAddItemPopoverOpen(false);
    },
    onError: (err) => toast.error(`Failed to add item: ${(err as Error).message}`)
  });

  const updateItemMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: PriceListItemUpdate }) => updatePriceListItem(id, data),
    onSuccess: () => {
      toast.success('Item pricing updated');
      queryClient.invalidateQueries({ queryKey: ['priceListItems', priceListId] });
      setIsEditFormOpen(false);
      setEditingItem(null);
    },
    onError: (err) => toast.error(`Update failed: ${(err as Error).message}`)
  });

  const removeItemMutation = useMutation({
    mutationFn: (id: number) => removePriceListItem(id),
    onSuccess: () => {
      toast.success('Item removed from price list');
      queryClient.invalidateQueries({ queryKey: ['priceListItems', priceListId] });
      setIsDeleteConfirmOpen(false);
      setDeletingItem(null);
    },
    onError: (err) => toast.error(`Removal failed: ${(err as Error).message}`)
  });

  // Event listeners for actions from the table
  useEffect(() => {
    if (!open) return;

    const handleEdit = (event: CustomEvent<PriceListItem>) => {
      if(event.detail.price_list_id === priceListId) {
        setEditingItem(event.detail);
        setIsEditFormOpen(true);
      }
    };
    
    const handleDelete = (event: CustomEvent<PriceListItem>) => {
      if(event.detail.price_list_id === priceListId) {
        setDeletingItem(event.detail);
        setIsDeleteConfirmOpen(true);
      }
    };

    document.addEventListener('edit-pricelist-item', handleEdit as EventListener);
    document.addEventListener('remove-pricelist-item', handleDelete as EventListener);

    return () => {
      document.removeEventListener('edit-pricelist-item', handleEdit as EventListener);
      document.removeEventListener('remove-pricelist-item', handleDelete as EventListener);
    };
  }, [open, priceListId]);

  // Add Item Logic
  const handleAddItem = () => {
    if (!selectedItemToAdd) {
      toast.error("Please select an item to add.");
      return;
    }

    // Check if any pricing detail was entered by the user
    const userProvidedPrice = newItemPrice !== null && newItemPrice !== undefined && newItemPrice !== '';
    const userProvidedDiscountPercent = newItemDiscountPercent !== null && newItemDiscountPercent !== undefined && newItemDiscountPercent !== '';
    const userProvidedDiscountAmount = newItemDiscountAmount !== null && newItemDiscountAmount !== undefined && newItemDiscountAmount !== '';

    const providedOptionsCount = [userProvidedPrice, userProvidedDiscountPercent, userProvidedDiscountAmount].filter(Boolean).length;

    if (providedOptionsCount > 1) {
      toast.error("Please specify only one pricing method (Rate, Disc %, or Disc Amt).");
      return;
    }

    // Base payload
    const basePayload: Partial<PriceListItemCreate> = {
      item_id: selectedItemToAdd.id,
      price_list_id: priceListId,
      is_tax_inclusive: newItemIsTaxInclusive,
      min_qty: 0, // Assuming default min_qty is 0, adjust if necessary
    };

    let finalPayload: PriceListItemCreate;

    if (providedOptionsCount === 0) {
      // Scenario 1: No pricing details provided by user - send only base info
      console.log("No pricing details provided, using default discount from backend.");
      finalPayload = basePayload as PriceListItemCreate;
    } else {
      // Scenario 2: User provided exactly one pricing detail
      console.log("User provided pricing details.");
      finalPayload = {
        ...basePayload,
        rate: userProvidedPrice ? parseFloat(newItemPrice) : null,
        discount_percent: userProvidedDiscountPercent ? parseFloat(newItemDiscountPercent) : null,
        discount_amount: userProvidedDiscountAmount ? parseFloat(newItemDiscountAmount) : null,
      } as PriceListItemCreate;

      // Validate numeric conversion
      if (userProvidedPrice && isNaN(finalPayload.rate!)) {
        toast.error("Invalid Rate value.");
        return;
      }
      if (userProvidedDiscountPercent && isNaN(finalPayload.discount_percent!)) {
        toast.error("Invalid Discount % value.");
        return;
      }
      if (userProvidedDiscountAmount && isNaN(finalPayload.discount_amount!)) {
        toast.error("Invalid Discount Amount value.");
        return;
      }
    }

    console.log("Final Add Item Payload:", finalPayload);
    addItemMutation.mutate(finalPayload);
  };

  // Edit Item Logic
  const handleEditSubmit = (data: PriceListItemUpdate) => {
    if(editingItem) {
      updateItemMutation.mutate({ id: editingItem.id, data });
    }
  };

  // Remove Item Logic
  const handleRemoveConfirm = () => {
    if(deletingItem) {
      removeItemMutation.mutate(deletingItem.id);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Manage Items in: {priceListName}</DialogTitle>
          <DialogDescription>Add, edit, or remove items and their specific pricing for this list.</DialogDescription>
        </DialogHeader>

        <div className="flex-1 flex flex-col min-h-0">
          {/* Add Item Section */}
          <div className="mb-4 p-4 border rounded-md bg-muted/40">
            <h3 className="text-lg font-medium mb-2">Add Item to List</h3>
            <div className="flex flex-wrap items-end gap-3">
              <Popover open={addItemPopoverOpen} onOpenChange={setAddItemPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={addItemPopoverOpen}
                    className="w-[300px] justify-between"
                  >
                    {selectedItemToAdd ? `${selectedItemToAdd.name} (${selectedItemToAdd.code ?? 'No Code'})` : "Select item..."}
                    <Loader2 className={`ml-2 h-4 w-4 shrink-0 ${isLoadingAllItems ? 'animate-spin' : 'opacity-0'}`} />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0">
                  <Command>
                    <CommandInput placeholder="Search items..." />
                    <CommandList>
                      <CommandEmpty>No items found.</CommandEmpty>
                      <CommandGroup>
                        <ScrollArea className="h-[200px]">
                          {availableItemsToAdd.map((item) => (
                            <CommandItem
                              key={item.id}
                              value={item.name}
                              onSelect={() => {
                                setSelectedItemToAdd(item);
                                setAddItemPopoverOpen(false);
                              }}
                            >
                              {item.name} {item.code && `(${item.code})`}
                            </CommandItem>
                          ))}
                        </ScrollArea>
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              {/* Pricing inputs for the selected item */}
              <Input 
                type="number" 
                step="0.01" 
                placeholder="Rate (Optional)" 
                value={newItemPrice} 
                onChange={e => setNewItemPrice(e.target.value)} 
                className="w-28" 
              />
              <Input 
                type="number" 
                step="0.01" 
                placeholder="Disc % (Optional)" 
                value={newItemDiscountPercent} 
                onChange={e => setNewItemDiscountPercent(e.target.value)} 
                className="w-28" 
              />
              <Input 
                type="number" 
                step="0.01" 
                placeholder="Disc Amt (Optional)" 
                value={newItemDiscountAmount} 
                onChange={e => setNewItemDiscountAmount(e.target.value)} 
                className="w-28" 
              />
              <div className="flex items-center space-x-2 pt-2">
                <Checkbox 
                  id="add-tax-inclusive" 
                  checked={newItemIsTaxInclusive} 
                  onCheckedChange={(checked) => setNewItemIsTaxInclusive(checked as boolean)} 
                />
                <label htmlFor="add-tax-inclusive" className="text-xs font-medium">Tax Inclusive</label>
              </div>

              <Button 
                onClick={handleAddItem} 
                disabled={!selectedItemToAdd || addItemMutation.isPending}
              >
                {addItemMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlusCircle className="mr-2 h-4 w-4" />}
                Add to List
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2 px-1">Select an item and set ONE pricing method (Rate, Disc %, or Disc Amt). Leave all blank to use the price list's default discount.</p>
          </div>

          <Separator />

          {/* Existing Items Table */}
          <div className="mt-4 flex-1 overflow-hidden">
            <ScrollArea className="h-full">
              <h3 className="text-lg font-medium mb-2 px-1">Items in this List</h3>
              {isListItemsError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>Failed to load items: {(listItemsError as Error)?.message}</AlertDescription>
                </Alert>
              )}
              <ReusableDataTable
                columns={columnsPriceListItem}
                data={priceListItemsResponse?.items ?? []}
                isLoading={isLoadingListItems}
                pagination={pagination}
                pageCount={pageCount}
                onPaginationChange={setPagination}
                sorting={sorting}
                onSortingChange={setSorting}
              />
            </ScrollArea>
          </div>
        </div>

        {/* Edit Form Dialog */}
        {editingItem && (
          <PriceListItemForm
            open={isEditFormOpen}
            onOpenChange={(open) => {
              setIsEditFormOpen(open);
              if(!open) setEditingItem(null);
            }}
            onSubmit={handleEditSubmit}
            defaultValues={editingItem}
            isLoading={updateItemMutation.isPending}
          />
        )}

        {/* Delete Confirmation */}
        <AlertDialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove Item from List?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to remove &quot;{deletingItem?.item?.name ?? 'this item'}&quot; from this price list? Its specific pricing rules for this list will be deleted.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={removeItemMutation.isPending}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleRemoveConfirm} 
                disabled={removeItemMutation.isPending} 
                className="bg-destructive hover:bg-destructive/90"
              >
                {removeItemMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};

export default PriceListItemManager; 